import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { DateRange } from "@/components/DateRange";
import { ReportView } from "@/components/ReportView";
import { api, download, errMsg, monthStartISO, todayISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMasters } from "@/hooks/useMasters";

const STATIC = {
  period_type: [["day", "Date-wise"], ["month", "Monthly"]],
  category: ["Commission", "Incentive", "Other", "Salary", "Rent/Office Expense", "Travel/Other Business Expense", "Other Business Expense",
    "Promoter Advance/Recoverable", "DSE Balance Transfer", "VI Virtual Account Transfer", "Bank/Cash Transfer", "Liability Settlement"].map((t) => [t, t]),
  account: [["Cash", "Cash"], ["Bank", "Bank"], ["UPI", "UPI"]],
  ledger: [["vi", "VI Virtual"], ["dse", "DSE"], ["fl", "FL"], ["cash", "Cash/Bank/UPI"], ["party", "Creditors/Debtors"]],
  payment_mode: [["Cash", "Cash"], ["UPI", "UPI"], ["Bank Transfer", "Bank Transfer"], ["Other", "Other"], ["Virtual", "Virtual transfer"]],
  linked: [["no", "Unlinked only"]],
  transaction_type: ["Opening", "Credit", "Debit", "Adjustment", "Balance Given", "Cash Received", "UPI Received", "Bank Received", "Other Received",
    "Advance", "Commission", "Recovery", "Commission Payment", "Receipt", "Payment", "Transfer", "Receivable Created", "Payment Received", "Payable Created", "Payment Made"].map((t) => [t, t]),
};
const LABEL = { retailer_id: "All retailers", period_type: "Date-wise", category: "All categories", party_id: "All parties", dse_id: "All DSEs", fl_id: "All FLs", account: "All categories", ledger: "All ledgers", payment_mode: "Any mode", transaction_type: "Any type", fund_account_id: "All Bank/UPI accounts", linked: "Linked + unlinked", distributor_id: "All 3 distributors" };
const TEXT = { q: "UTR / UPI ID / account / name", amount: "Amount ₹" };

function TextFilter({ name, value, onCommit, testId }) {
  const [v, setV] = useState(value || "");
  return (
    <Input value={v} placeholder={TEXT[name]} onChange={(e) => setV(e.target.value)} onBlur={() => onCommit(v)}
      onKeyDown={(e) => e.key === "Enter" && onCommit(v)} className={`h-9 ${name === "q" ? "w-[240px]" : "w-[120px] num"}`}
      type={name === "amount" ? "number" : "text"} data-testid={testId} />
  );
}

export function ExportButtons({ path, params, name }) {
  const [busy, setBusy] = useState("");
  const go = async (fmt) => {
    setBusy(fmt);
    try {
      await download(path, { ...params, format: fmt }, `ayzal_${name}_${params.frm}_${params.to}.${fmt}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={() => go("xlsx")} disabled={!!busy} data-testid={`${name}-export-excel`}>
        {busy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-emerald-600" />} Excel
      </Button>
      <Button variant="outline" size="sm" onClick={() => go("pdf")} disabled={!!busy} data-testid={`${name}-export-pdf`}>
        {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />} PDF
      </Button>
    </div>
  );
}

export function ReportPanel({ reportKey, filters = [], initialFilters = {} }) {
  const { version } = useAuth();
  const masters = useMasters();
  const [frm, setFrm] = useState(monthStartISO());
  const [to, setTo] = useState(todayISO());
  const [f, setF] = useState(initialFilters);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = { frm, to, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "__all")) };
  const key = JSON.stringify(params);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/${reportKey}`, { params: JSON.parse(key) })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [reportKey, key, version]);

  const opts = (name) => {
    if (name === "dse_id") return masters.dse.map((m) => [m.dse_id, `${m.dse_id} · ${m.dse_name}`]);
    if (name === "fl_id") return masters.fl.map((m) => [m.fl_id, `${m.fl_id} · ${m.fl_name}`]);
    if (name === "party_id") return masters.party.map((m) => [m.party_id, `${m.party_id} · ${m.party_name}`]);
    if (name === "retailer_id") return masters.retailer.map((m) => [m.retailer_id, `${m.retailer_id} · ${m.retailer_name}`]);
    if (name === "distributor_id") return masters.distributors.map((d) => [d.code, d.name]);
    if (name === "fund_account_id") return masters.accounts.map((a) => [a.account_id, `${a.account_name}${a.upi_id ? ` · ${a.upi_id}` : ""}`]);
    return STATIC[name];
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3 bg-white border border-slate-200 rounded-xl p-3">
        <div className="flex flex-wrap items-end gap-2">
          <DateRange frm={frm} to={to} onChange={(a, b) => { setFrm(a); setTo(b); }} testPrefix={`${reportKey}-range`} />
          {filters.map((name) => TEXT[name] ? (
            <TextFilter key={name} name={name} value={f[name]} onCommit={(v) => setF((s) => ({ ...s, [name]: v }))} testId={`${reportKey}-filter-${name}`} />
          ) : (
            <Select key={name} value={f[name] || "__all"} onValueChange={(v) => setF((s) => ({ ...s, [name]: v }))}>
              <SelectTrigger className="h-9 w-[180px]" data-testid={`${reportKey}-filter-${name}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">{LABEL[name]}</SelectItem>
                {opts(name).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          ))}
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400 mb-2.5" />}
        </div>
        <ExportButtons path={`/reports/${reportKey}`} params={params} name={reportKey} />
      </div>
      {report && (
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900" data-testid={`${reportKey}-title`}>{report.title}</h2>
          <p className="text-xs text-slate-500 mb-3">{report.subtitle}</p>
          <ReportView report={report} testId={reportKey} />
        </div>
      )}
    </div>
  );
}
